import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import TablePagination from '@mui/material/TablePagination';
import { useEffect, useState } from 'react';
import { Icon } from 'ui/styled';
import { Content, PaginationContainer, PaginationQuantity } from './styled';

// --- Estilização da Paginação ---
const CustomPagination = styled(TablePagination)(({ theme }) => ({
  // *AJUSTE DE TAMANHO DE FONTE* Aumentamos para 1rem para maior destaque e padronização.
  fontSize: '1rem',
  // O root do TablePagination vem com `overflow: auto` do MUI, e num container
  // flex isso zera o tamanho minimo automatico do item — a paginacao era
  // espremida e ganhava barra de rolagem propria quando faltava altura. Quem
  // deve encolher e a tabela, nunca a paginacao.
  flexShrink: 0,
  overflow: 'visible',
  '& .MuiTablePagination-actions': {
    display: 'flex',
    marginLeft: 0,
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexShrink: 0,
  },
  '& .MuiButtonBase-root': {
    margin: theme.spacing(0.25),
    // Botões 36x36px: barra mais estreita, ainda dentro do alvo de toque
    minWidth: 36,
    height: 36,
    backgroundColor: theme.palette.colors.backgroundgrey,
    color: theme.palette.colors.black,
    '&.Mui-selected': {
      color: theme.palette.colors.white,
      backgroundColor: theme.palette.blue.main,
      '&:hover': {
        backgroundColor: theme.palette.blue.dark,
      },
    },
    '&:hover': {
      backgroundColor: theme.palette.colors.lightgrey,
    },
    '&:disabled': {
      backgroundColor: theme.palette.colors.lightgrey,
      color: theme.palette.colors.grey,
    },
    // Reticencia: clicavel, mas sem fundo, para nao ser confundida com pagina.
    '&.pagination-ellipsis': {
      backgroundColor: 'transparent',
      color: theme.palette.colors.grey,
      minWidth: 26,
      fontSize: '1.1rem',
      lineHeight: 1,
      paddingBottom: 6,
      '&:hover': {
        backgroundColor: theme.palette.colors.backgroundgrey,
        color: theme.palette.colors.black,
      },
    },
  },
  '& .MuiTablePagination-toolbar': {
    padding: 0,
    minHeight: 44,
    justifyContent: 'flex-start', // Deixa o select e sua label à esquerda
    '& .MuiTablePagination-spacer': {
      display: 'none',
    },
    // *GARANTINDO PADRÃO* A label "Linhas por página" herda o novo tamanho de fonte.
    '& .MuiTablePagination-selectLabel': {
      alignItems: 'center',
      fontSize: '15px', // Essencial para o mesmo padrão do "Exibindo..."
      // marginRight: theme.spacing(1),
      lineHeight: '10px', // Alinha verticalmente com o select de 30px
      justifyContent: 'flex-start',
      textAlign: 'center',
      marginTop: '10px',
      fontFamily: 'Kumbh Sans',
      fontWeight: 600,
    },
    // Esconde o displayedRows padrão
    '& .MuiTablePagination-displayedRows': {
      display: 'none',
    },
    '& .MuiTablePagination-selectRoot': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  // *AJUSTE DO QUADRADO SELECT* Acompanha os botões de 36px.
  '& .MuiTablePagination-select': {
    width: '62px',
    height: '30px',
    lineHeight: '30px',
    textAlign: 'center',
    padding: '0 24px 0 8px !important', // *AJUSTE* Padding para garantir espaço e centralização
    boxSizing: 'border-box',
    border: `1px solid ${theme.palette.colors.grey}`,
    // borderRadius: theme.shape.borderRadius,
    borderRadius: 5,
    backgroundColor: theme.palette.colors.backgroundgrey,
    fontSize: 'inherit', // Garante que o número dentro do select também use 1rem
    '&:focus': {
      backgroundColor: theme.palette.colors.backgroundgrey,
    },
  },
  // Ajuste do ícone para melhor posicionamento no quadrado do select
  '& .MuiTablePagination-selectIcon': {
    top: 'calc(50% - 10px)',
    right: '4px',
  },
}));

// --- Truncamento das paginas ---
// A barra mostra sempre a mesma quantidade de caixas, para nao mudar de
// largura conforme o operador navega. Nos extremos o bloco de paginas cresce e
// ocupa o lugar da reticencia que nao aparece.
//
// Ha um unico botao de ajuste: SIBLING_COUNT. O total de caixas sai da conta
// SIBLING_COUNT * 2 + 5 — a atual, as vizinhas dos dois lados, a primeira, a
// ultima e as duas reticencias. Derivar em vez de fixar evita a barra mudar de
// largura por os dois valores ficarem em desacordo.
const SIBLING_COUNT = 1; // paginas mostradas de cada lado da pagina atual, no meio da lista
const SLOTS = SIBLING_COUNT * 2 + 5; // caixas visiveis: 7 com uma vizinha, 9 com duas
const JUMP_SIZE = 5; // quantas paginas a reticencia avanca ou volta ao ser clicada

// Tamanho do bloco corrido: as SLOTS caixas menos a reticencia e a pagina
// oposta, que ficam do outro lado.
const BLOCK_SIZE = SLOTS - 2;

const range = (start, end) =>
  Array.from({ length: end - start + 1 }, (_, index) => start + index);

// Recebe e devolve paginas em base 1. As reticencias saem como as strings
// 'prev' e 'next', para a renderizacao saber para que lado saltar.
export const buildPageItems = (current, total) => {
  if (total <= SLOTS) {
    return range(1, total);
  }
  // Perto do inicio: 1 2 3 ... total
  if (current <= BLOCK_SIZE) {
    return [...range(1, BLOCK_SIZE), 'next', total];
  }
  // Perto do fim: 1 ... total-2 total-1 total
  if (current > total - BLOCK_SIZE) {
    return [1, 'prev', ...range(total - BLOCK_SIZE + 1, total)];
  }
  // No meio: 1 ... current-1 current current+1 ... total
  return [
    1,
    'prev',
    ...range(current - SIBLING_COUNT, current + SIBLING_COUNT),
    'next',
    total,
  ];
};

// --- Componente de Paginação ---
export default function CustomTablePagination(props) {
  const {
    count,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    rowsPerPageOptions = [10, 25, 50, 100],
  } = props;
  const totalPages = Math.ceil(count / rowsPerPage);
  const [displayedRows, setDisplayedRows] = useState(0);

  useEffect(() => {
    const start = page * rowsPerPage;
    const end = Math.min(count, start + rowsPerPage);
    setDisplayedRows(end - start);
  }, [page, rowsPerPage, count]);

  return (
    <CustomPagination
      component="div"
      count={count}
      page={page}
      onPageChange={onPageChange}
      rowsPerPage={rowsPerPage}
      rowsPerPageOptions={rowsPerPageOptions}
      onRowsPerPageChange={onRowsPerPageChange}
      labelRowsPerPage="Linhas por página:"
      labelDisplayedRows={() => null}
      ActionsComponent={(subProps) => (
        <PaginationContainer>
          {/* O texto "Exibindo resultados" está aqui, alinhado à direita e usando 1rem */}
          <PaginationQuantity
            style={{
              fontSize: '15px',
              marginRight: '12px',
              fontFamily: 'Kumbh Sans',
            }}
          >
            {`Exibindo ${displayedRows} de ${count} resultados`}
          </PaginationQuantity>
          <Content className={subProps.className}>
            <Button
              onClick={(event) =>
                subProps.onPageChange(event, subProps.page - 1)
              }
              disabled={subProps.page === 0}
            >
              <Icon icon="arrow-left" nomargin />
            </Button>
            {buildPageItems(subProps.page + 1, totalPages).map(
              (item, index) => {
                // Reticencia: salta um bloco em vez de so decorar.
                if (typeof item === 'string') {
                  const target =
                    item === 'prev'
                      ? Math.max(0, subProps.page - JUMP_SIZE)
                      : Math.min(totalPages - 1, subProps.page + JUMP_SIZE);
                  return (
                    <Button
                      key={`${item}-${index}`}
                      className="pagination-ellipsis"
                      title={
                        item === 'prev'
                          ? `Voltar ${JUMP_SIZE} páginas`
                          : `Avançar ${JUMP_SIZE} páginas`
                      }
                      aria-label={
                        item === 'prev'
                          ? `Voltar ${JUMP_SIZE} páginas`
                          : `Avançar ${JUMP_SIZE} páginas`
                      }
                      onClick={(event) => subProps.onPageChange(event, target)}
                    >
                      …
                    </Button>
                  );
                }
                return (
                  <Button
                    key={item}
                    onClick={(event) => subProps.onPageChange(event, item - 1)}
                    className={subProps.page === item - 1 ? 'Mui-selected' : ''}
                  >
                    {item}
                  </Button>
                );
              }
            )}
            <Button
              onClick={(event) =>
                subProps.onPageChange(event, subProps.page + 1)
              }
              disabled={subProps.page >= totalPages - 1}
            >
              <Icon icon="arrow-right" nomargin />
            </Button>
          </Content>
        </PaginationContainer>
      )}
    />
  );
}
